import React, { HTMLAttributes, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { observer } from "mobx-react-lite";
import cx from "classnames";
import { Hooks, Util } from "@xt/xtbase";
const { useTranslation } = Hooks;
const { moment } = Util;
// import store from "store";

import DatePicker from "components/react-mobile-datepicker/index";

import styles from "./index.module.scss";

interface Props extends Omit<HTMLAttributes<HTMLDivElement>, "onChange"> {
  startTime: number | undefined;
  setStartTime: (arg: number) => void;
  endTime: number | undefined;
  setEndTime: (arg: number) => void;
}

const Main: React.FC<Props> = ({ startTime, setStartTime, endTime, setEndTime }) => {
  const t = useTranslation();

  const startTimeLab = useMemo(() => {
    if (!startTime || startTime === 0) return "";
    return moment(startTime).format("YYYY-MM-DD");
  }, [startTime]);
  const endTimeLab = useMemo(() => {
    if (!endTime || endTime === 0) return "";
    return moment(endTime).format("YYYY-MM-DD");
  }, [endTime]);
  
  const placeholder = t("trade.date") || "Select date";

  const [open, setOpen] = useState(false);
  const [time, setTime] = useState<Date>();
  const [min, setMin] = useState<Date>();
  const [max, setMax] = useState<Date>();

  const type = useRef<string>();

  const handleOpen = useCallback((time) => {
    setTime(new Date(time));
    setOpen(true);
  }, []);
  const handleClose = useCallback(() => {
    setOpen(false);
  }, []);
  const handleSelect = useCallback((date) => {
    const timestamp = date.valueOf();
    // console.log(date, timestamp);
    if (type.current === "start") {
      setStartTime(timestamp);
    } else if (type.current === "end") {
      setEndTime(timestamp);
    }
  }, []);

  const handleClickBtnStart = useCallback(() => {
    type.current = "start";
    // 如果startTime为0或无效，使用当前时间
    const currentTime = startTime && startTime > 0 ? startTime : moment().valueOf();
    handleOpen(currentTime);

    const todayEnd = moment().endOf("day").valueOf();
    const endTimeValue = endTime && endTime > 0 ? endTime : todayEnd;
    // 设置最大值为结束时间或今天
    const maxDate = new Date(Math.min(endTimeValue, todayEnd));
    // 设置最小值为90天前，但为了允许年份切换，设置年份范围为更宽的范围
    // 最小日期：当前年份的1月1日（但实际限制在90天前）
    const minDate = moment().subtract(90, "days").startOf("day").toDate();
    // 为了允许年份切换，将最小年份设置为当前年份减去2年
    // 这样用户可以在合理的年份范围内切换
    const minYear = moment().subtract(2, "years").year();
    minDate.setFullYear(minYear, 0, 1); // 设置为该年的1月1日
    
    setMax(maxDate);
    setMin(minDate);
  }, [startTime, endTime, handleOpen]);
  const handleClickBtnEnd = useCallback(() => {
    type.current = "end";
    // 如果endTime为0或无效，使用当前时间
    const currentTime = endTime && endTime > 0 ? endTime : moment().valueOf();
    handleOpen(currentTime);

    const todayEnd = moment().endOf("day").valueOf();
    const startTimeValue = startTime && startTime > 0 ? startTime : moment().subtract(90, "days").startOf("day").valueOf();
    // 设置最大值为今天或开始时间+90天
    const maxDate = new Date(Math.min(todayEnd, moment(startTimeValue).add(90, "days").endOf("day").valueOf()));
    // 设置最小值为开始时间，但为了允许年份切换，设置年份范围为更宽的范围
    const minDate = new Date(startTimeValue);
    // 为了允许年份切换，将最大年份设置为当前年份加上1年
    // 这样用户可以在合理的年份范围内切换
    const maxYear = moment().add(1, "year").year();
    maxDate.setFullYear(maxYear, 11, 31); // 设置为该年的12月31日
    
    setMax(maxDate);
    setMin(minDate);
  }, [startTime, endTime, handleOpen]);

  return (
    <>
      <div className={styles.main}>
        <div>
          <button className={cx("btnTxt")} onClick={handleClickBtnStart} data-placeholder={placeholder}>
            {startTimeLab || <span style={{ color: "var(--xt-theme-text-color-04)" }}>{placeholder}</span>}
          </button>
          <span>{t("trade.to")}</span>
          <button className={cx("btnTxt")} onClick={handleClickBtnEnd} data-placeholder={placeholder}>
            {endTimeLab || <span style={{ color: "var(--xt-theme-text-color-04)" }}>{placeholder}</span>}
          </button>
        </div>
      </div>
      <DatePicker 
        open={open} 
        value={time} 
        min={min} 
        max={max} 
        onClose={handleClose} 
        onSelect={handleSelect}
        dateConfig={{
          year: {
            format: 'YYYY',
            caption: 'Year',
            step: 1
          },
          month: {
            format: 'M',
            caption: 'Mon',
            step: 1
          },
          date: {
            format: 'D',
            caption: 'Day',
            step: 1
          }
        }}
      />
    </>
  );
};

export default observer(Main);
// export default Main;
